import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Image as ImageIcon } from 'lucide-react';

import { getRewardGallery, updateCategory } from '../utils/api';
import { LoadingIndicator } from './assets';

export default function CategoryImageDialog({ category, open, onClose, onSaved }) {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    setLoading(true);
    setError('');
    getRewardGallery({ limit: 100 })
      .then((response) => {
        if (alive) setImages(response.images || []);
      })
      .catch((err) => {
        if (alive) setError(err.data?.message || err.message || 'Unable to load images.');
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => { alive = false; };
  }, [open]);

  const selectImage = async (imageId) => {
    setSaving(true);
    setError('');
    try {
      const saved = await updateCategory(category.id, { image_id: imageId });
      onSaved(saved);
      onClose();
    } catch (err) {
      setError(err.data?.message || err.message || 'Unable to update the category image.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ color: 'primary.main' }}>Category image</DialogTitle>
      <DialogContent sx={{ pt: '16px !important' }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {loading ? <LoadingIndicator text="loading images" /> : (
          <Grid container spacing={1.5}>
            <Grid size={{ xs: 4, sm: 3 }}>
              <Button
                fullWidth
                disabled={saving}
                onClick={() => selectImage(null)}
                aria-label="Use no category image"
                sx={(theme) => ({
                  m: 0,
                  aspectRatio: '1 / 1',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  color: theme.palette.secondary.main,
                  border: `2px solid ${category?.image_id == null
                    ? (theme.palette.green?.main || '#39FF14')
                    : theme.palette.primary.main}`,
                  backgroundColor: theme.palette.background.default,
                })}
              >
                <ImageIcon size={24} />
                <Typography variant="caption" sx={{ color: 'secondary.main' }}>No image</Typography>
              </Button>
            </Grid>
            {images.map((image) => (
              <Grid key={image.id} size={{ xs: 4, sm: 3 }}>
                <Box
                  component="button"
                  type="button"
                  disabled={saving}
                  aria-label={`Use category image ${image.id}`}
                  onClick={() => selectImage(image.id)}
                  sx={(theme) => ({
                    p: 0,
                    m: 0,
                    width: '100%',
                    aspectRatio: '1 / 1',
                    overflow: 'hidden',
                    cursor: 'pointer',
                    borderRadius: 3,
                    border: `3px solid ${category?.image_id === image.id
                      ? (theme.palette.green?.main || '#39FF14')
                      : alpha(theme.palette.primary.main, 0.45)}`,
                    backgroundColor: theme.palette.background.default,
                  })}
                >
                  <Box
                    component="img"
                    src={image.thumbnail_url}
                    alt=""
                    sx={{ width: '100%', height: '100%', display: 'block', objectFit: 'cover' }}
                  />
                </Box>
              </Grid>
            ))}
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
}
